import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch, RootState } from "../../../../store";
import {
  getSuggestions,
  clearSuggestions,
} from "../../../../Redux/Search_apis/slice";
import { SuggestionResponseDto } from "../../../../Redux/Search_apis/types";
import {
  recordActivity,
  getRecentSearches,
} from "../../../../Redux/ActivityApi/slice";
import { ActivityType } from "../../../../Redux/ActivityApi/types";
import AccountMenu from "../accountmenu/AccountMenu";
import LanguageCurrency from "../lang-currecy/LanguageCurrency";
import { useAuth } from "../../../../context/AuthContext";
import { useCart } from "../../../../context/CartContext";
import Delivery from "../deliver/Delivery";
import MobileSearchInput from "./MobileSearchInput";

const Search = () => {
  const { isAuthenticated , user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuperCategory, setSelectedSuperCategory] = useState("");
  const [showCatDropdown, setShowCatDropdown] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const suggestions = useSelector(
    (state: RootState) => state.search.suggestions.data
  );
  const recentSearches = useSelector(
    (state: RootState) => state.activity.recentSearches
  );
  const { superCategories } = useSelector((state: RootState) => state.filters);
  const { cart } = useCart();
  const cartCount =
    cart?.itemsCount ??
    cart?.items?.reduce((total, item) => total + (item.quantity || 1), 0) ??
    0;
  const suggestionRef = useRef<HTMLDivElement>(null);
  const catDropdownRef = useRef<HTMLDivElement>(null);

  const loadRecentSearches = () => {
    dispatch(getRecentSearches(5));
  };
   

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchTerm.trim().length > 1) {
        dispatch(getSuggestions(searchTerm));
        setShowSuggestions(true);
      } else {
        dispatch(clearSuggestions());
      }
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, dispatch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (suggestionRef.current && !suggestionRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
      if (catDropdownRef.current && !catDropdownRef.current.contains(event.target as Node)) {
        setShowCatDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = async (q: string) => {
    if (q.trim()) {
      await dispatch(
        recordActivity({
          activities: {
            eventType: ActivityType.SEARCH,
            searchQuery: q.trim(),
          },
        })
      );
      const superCat = selectedSuperCategory || "All";
      const params = new URLSearchParams({ q: q.trim() });
      navigate(`/Categories/${encodeURIComponent(superCat)}?${params.toString()}`);
      setShowSuggestions(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch(searchTerm);
  };

  const catList: { name: string; image?: string }[] = superCategories.data.length > 0
    ? superCategories.data.map((c: any) => ({ name: c.name ?? "", image: c.image }))
    : [];

  return (
    <div ref={suggestionRef} className="w-100">
      <div className=" display-max">
        <div className=" d-flex justify-content-between align-items-center">
          <div className=" w-20">
            <a href="/">
              <img
                src="/assets/img/salarlogo.png"
                height="100"
                width="100"
                alt=""
              />
            </a>
          </div>
        <div className=" w-80">
          <div
            className="collapse navbar-collapse justify-content-center"
            id="navigation"
          >
            <div className=" d-flex justify-content-start bg-white align-items-center h-medium border border-radius-lg w-80 position-relative">
              {/* Category Selector */}
              <div ref={catDropdownRef} className="position-relative w-30">
                <div
                  className="background px-2 p-1 lh-0 cursor-pointer"
                  onClick={() => setShowCatDropdown((prev) => !prev)}
                >
                  <p className=" text-black fw-bold text-xs mt-3 ms-2 text-nowrap">
                    {selectedSuperCategory || "All Categories"}
                  </p>
                  <div className=" mt-n3 mb-2">
                    <span className=" text-black-50 fw-normal">
                      <div className=" input-group input-group-sm">
                        <input
                          type="text"
                          className=" form-control text-xs"
                          placeholder=" Search for Categories"
                          readOnly
                        />
                      </div>
                    </span>
                  </div>
                </div>
                {showCatDropdown && (
                  <div
                    className="position-absolute start-0 bg-white shadow-lg border-radius-2xl p-2 px-3 overflow-auto"
                    style={{ top: "100%", minWidth: "220px", maxHeight: "380px", zIndex: 1060 }}
                  >
                    {/* All Categories option */}
                    <div
                      className={`lh-base mt-2 dropdown-item border-radius-md mb-2 p-1 cursor-pointer ${!selectedSuperCategory ? "bg-light fw-bold" : ""}`}
                      style={{ transition: "background 0.15s" }}
                      onClick={() => { setSelectedSuperCategory(""); setShowCatDropdown(false); }}
                    >
                      <div className=" d-flex justify-content-lg-start align-items-center gap-2">
                        <div>
                          <li className="avatar avatar-sm border border-radius-lg list-unstyled bg-light d-flex align-items-center justify-content-center">
                            <i className="ri-apps-line text-dark" style={{ fontSize: 14 }}></i>
                          </li>
                        </div>
                        <div>
                          <div>
                            <b className="fs-6 text-sm fw-normal text-black">All Categories</b>
                          </div>
                        </div>
                      </div>
                    </div>
                    {catList.map(({ name, image }) => (
                      <div
                        key={name}
                        className={`lh-base mt-2 dropdown-item border-radius-md mb-2 p-1 cursor-pointer ${selectedSuperCategory === name ? "bg-light fw-bold" : ""}`}
                        style={{ transition: "background 0.15s" }}
                        onClick={() => { setSelectedSuperCategory(name); setShowCatDropdown(false); }}
                      >
                        <div className=" d-flex justify-content-lg-start align-items-center gap-2">
                          <div>
                            <li className="avatar avatar-sm border border-radius-lg list-unstyled overflow-hidden bg-light d-flex align-items-center justify-content-center">
                              {image ? (
                                <img
                                  src={image}
                                  alt={name}
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                                />
                              ) : (
                                <i className="ri-flag-line text-muted" style={{ fontSize: 14 }}></i>
                              )}
                            </li>
                          </div>
                          <div>
                            <div>
                              <b className="fs-6 text-sm fw-normal text-black">{name}</b>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <span className=" text-gray opacity-2">|</span>
              <div className=" w-100">
                <div className="background d-flex justify-content-between px-2 align-items-center">
                  <div className="background lh-0 w-100 cursor-pointer">
                    <span className=" text-black-50 fw-normal">
                      <div className=" input-group h-small input-group-sm">
                        <input
                          type="text"
                          className=" form-control fw-normal fs-6 ms-1"
                          placeholder=" Search for Products, Brands and More"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          onKeyDown={onKeyDown}
                          onFocus={() => {
                            setShowSuggestions(true);
                            loadRecentSearches();
                          }}
                        />
                      </div>
                    </span>
                  </div>
                  <div>
                    <span
                      className="input-group-text p-2 cursor-pointer w-auto rounded-circle btn btn-md btn-dark"
                      onClick={() => handleSearch(searchTerm)}
                    >
                      <i className="bi bi-search text-white p-1 text-sm"></i>
                    </span>
                  </div>
                </div>
              </div>

              {/* Suggestions Dropdown */}
              {showSuggestions &&
                (suggestions.length > 0 ||
                  (searchTerm.trim().length === 0 &&
                    recentSearches.length > 0)) && (
                  <div
                    className="position-absolute top-100 start-0 w-100 bg-white shadow-lg border-radius-lg mt-1 z-index-3 overflow-hidden"
                    style={{ maxHeight: "300px", overflowY: "auto" }}
                  >
                    {searchTerm.trim().length > 1
                      ? suggestions.map(
                          (suggestion: SuggestionResponseDto, index: number) => (
                            <div
                              key={index}
                              className="p-3 border-bottom border-light cursor-pointer hover-bg-light d-flex align-items-center"
                              onClick={() => {
                                setSearchTerm(suggestion.text);
                                handleSearch(suggestion.text);
                              }}
                            >
                              <i className="bi bi-search text-muted me-3"></i>
                              <div>
                                <h6 className="mb-0 text-sm fw-bold">
                                  {suggestion.text}
                                </h6>
                                {suggestion.metadata?.categoryHierarchy
                                  ?.category && (
                                  <small className="text-xs text-muted">
                                    In{" "}
                                    {
                                      suggestion.metadata.categoryHierarchy
                                        .category
                                    }
                                  </small>
                                )}
                              </div>
                            </div>
                          )
                        )
                      : recentSearches.length > 0 && (
                          <>
                            <div className="p-2 px-3 border-bottom bg-light">
                              <small className="text-uppercase text-muted fw-bold text-xs">
                                Recent Searches
                              </small>
                            </div>
                            {recentSearches.map(
                              (search: string, index: number) => (
                                <div
                                  key={index}
                                  className="p-3 border-bottom border-light cursor-pointer hover-bg-light d-flex align-items-center"
                                  onClick={() => {
                                    setSearchTerm(search);
                                    handleSearch(search);
                                  }}
                                >
                                  <i className="bi bi-clock-history text-muted me-3"></i>
                                  <h6 className="mb-0 text-sm">{search}</h6>
                                </div>
                              )
                            )}
                          </>
                        )}
                  </div>
                )}
            </div>
          </div>
        </div>
        <div>
          <ul className="navbar-nav ms-auto gap-2 align-items-center">
            <Delivery />
            <LanguageCurrency />
            <li className="avatar avatar-sm">
              <a href=".">
                <i className="bi bi-bell fs-4 text-dark"></i>
              </a>
            </li>
            <li className="avatar avatar-sm">
              <a href="/Cart">
                <i className="bi bi-bag text-black fs-4"></i>
                <span className="position-absolute top-35 translate-middle p-1 bg-dark cart-items text-center border border-light rounded-circle">
                  <div className=" mt-n1">
                    <b className=" text-white text-xxs fw-normal">
                      {cartCount}
                    </b>
                  </div>
                </span>
              </a>
            </li>
            {isAuthenticated ? (
              <div className="avatar cutsom-avatar cursor-pointer border border-radius-lg ms-1 dropdown dropdown-hover">
                <div
                  id="dropdownMenuButton"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <div className=" d-flex justify-content-evenly align-items-end gap-2">
                    <div>
                      <div>
                        <i className="fi fi-rr-menu-burger fs-6 ms-2 text-black"></i>
                      </div>
                    </div>
                    <div>
                      <div>
                        <span className="input-group-text p-1 cursor-pointer w-auto rounded-circle btn btn-md btn-dark">
                          <div className="w-22">
                            <b className=" p-1 fs-6">{user?.fullName?.charAt(0).toUpperCase()}</b>
                          </div>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <AccountMenu />
              </div>
            ) : (
              <div className="d-flex align-items-center gap-2">
                <a
                  href="/SignIn"
                  className="btn btn-outline-dark btn-sm px-4 py-2 rounded-pill fw-semibold transition-all"
                >
                  Login
                </a>
              </div>
            )}
          </ul>
        </div>
      </div>
      </div>
      <div className=" display-mini display-mob w-100 position-relative">
        <MobileSearchInput
          placeholder="Search for Electronics"
          inputGroupClass="input-group input-group-lg bg-white border-radius-lg border-0 d-flex align-items-center"
        />
      </div>
    </div>
  );
};

export default Search;
